<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <exo-input-name :root="_self" />
        <exo-input-double :root="_self" name="platformMass" />
        <v-row>
          <v-col cols="12">
            <v-divider />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="6">
                <exo-input-ref-notparametric
                  :root="_self"
                  :onChangeCallback="dutyCycleUpdate"
                  name="thruster"
                  type="thruster"
                  componentRoute="/numericalthruster"
                />
              </v-col>
              <v-col cols="6">
                <exo-input-slider
                  v-if="
                    inputs.thruster != null &&
                    inputs.thruster.propulsionSystemType === 'CHEMICAL'
                  "
                  :min="3"
                  :max="getMaxThrustDuration()"
                  :root="_self"
                  name="thrustDuration"
                >
                  <exo-help-dialog title="Maximum thrust duration">
                    <tooltipThrustDuration />
                  </exo-help-dialog>
                </exo-input-slider>
                <exo-input-double
                  :root="_self"
                  v-if="
                    inputs.thruster != null &&
                    inputs.thruster.propulsionSystemType === 'ELECTRICAL'
                  "
                  :onChangeCallback="dutyCycleUpdate"
                  name="onBoardAveragePower"
                >
                  <exo-help-dialog title="Orbital average power for propulsion">
                    <tooltipOnBoardAveragePower />
                  </exo-help-dialog>
                </exo-input-double>
                <exo-input-slider
                  v-if="
                    inputs.thruster != null &&
                    inputs.thruster.propulsionSystemType === 'ELECTRICAL'
                  "
                  :max="getMaxDutyCycle()"
                  :root="_self"
                  name="dutyCycle"
                >
                  <exo-help-dialog margin="mt-0 ml-2" title="Duty cycle">
                    <tooltipDutyCycleAnalytical />
                    <tooltipDutyCycleNumerical />
                  </exo-help-dialog>
                </exo-input-slider>
                <exo-form-alert
                  type="warning"
                  :onChangeCallback="getMaxDeltaV"
                  v-if="
                    inputs.thruster != null &&
                    inputs.thruster.propulsionSystemType === 'CHEMICAL'
                  "
                >
                  Firing <katex-element expression="\Delta V" /> correspond to
                  {{ this.getMaxDeltaV() }} m/s
                  <br />
                </exo-form-alert>
                <exo-form-alert
                  type="warning"
                  v-if="
                    inputs.thruster != null &&
                    inputs.thruster.propulsionSystemType === 'ELECTRICAL' &&
                    inputs.dutyCycle > 60
                  "
                >
                  A high orbital duty cycle might be inefficient when changing
                  the inclination of an orbit. Please have a look at the help
                  item.
                  <br />
                </exo-form-alert>
                <exo-form-alert
                  type="warning"
                  v-if="
                    inputs.thruster != null &&
                    inputs.thruster.propulsionSystemType === 'ELECTRICAL' &&
                    inputs.dutyCycle > getMaxUnalteredDutyCycle()
                  "
                >
                  Due to the thruster warm-up, the value of the duty cycle
                  effectively used in the simulation may be smaller than
                  {{ inputs.dutyCycle }} %. Please have a look at the help item.
                </exo-form-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="6">
                <exo-input-checkbox :root="_self" name="powerSystemDefined" />
                <exo-input-checkbox :root="_self" name="dragModelDefined" />
                <exo-input-checkbox :root="_self" name="srpModelDefined" />
              </v-col>
              <v-col cols="6">
                <exo-input-checkbox
                  v-if="inputs.powerSystemDefined"
                  :root="_self"
                  name="operationSystemDefined"
                >
                  <exo-help-dialog
                    margin="mt-0 ml-2"
                    title="Additional systems definition"
                  >
                    <tooltipAdditionalSystem />
                  </exo-help-dialog>
                </exo-input-checkbox>
                <exo-form-alert
                  type="warning"
                  v-if="
                    inputs.powerSystemDefined && inputs.operationSystemDefined
                  "
                >
                  Additional systems will not be used for
                  <b>Maneuvering Strategy Design </b>, nor
                  <b> Orbit Extrapolation </b>.
                </exo-form-alert>
                <exo-input-checkbox :root="_self" name="attitudeModelDefined" />
              </v-col>
            </v-row>
            <v-divider />
          </v-col>
        </v-row>
        <div
          v-if="
            inputs.powerSystemDefined ||
            inputs.srpModelDefined ||
            inputs.dragModelDefined
          "
        >
          <exo-input-ref-notparametric
            :root="_self"
            name="spacecraftGeometry"
            type="spacecraftGeometry"
            componentRoute="/numericalspacecraftgeometry"
          >
            <exo-help-dialog title="Spacecraft shape">
              <tooltipSpacecraftShape />
            </exo-help-dialog>
          </exo-input-ref-notparametric>

          <exo-form-alert
            type="error"
            v-if="
              inputs.powerSystemDefined &&
              inputs.spacecraftGeometry &&
              inputs.spacecraftGeometry.type === 'SPHERE'
            "
          >
            Error: a spherical geometry can not be used to simulate the system.
          </exo-form-alert>
          <exo-form-alert
            type="warning"
            v-if="
              inputs.operationSystemDefined &&
              inputs.spacecraftGeometry &&
              !inputs.spacecraftGeometry.pointingOperationSystemDefined
            "
          >
            Operation system is defined but its direction is not set in the
            current shape.
          </exo-form-alert>
        </div>
        <v-row v-if="inputs.powerSystemDefined">
          <v-col cols="12">
            <v-row>
              <v-col cols="6">
                <h3>Power system model</h3>
                <br />
                <exo-input-double :root="_self" name="platformPower">
                  <exo-help-dialog title="Platform power">
                    <tooltipPlatformPower />
                  </exo-help-dialog>
                </exo-input-double>
                <exo-input-ref-notparametric
                  :root="_self"
                  name="batteryDefinition"
                  type="batteryDefinition"
                  componentRoute="/numericalbatterydefinition"
                />
              </v-col>
              <v-col v-if="inputs.operationSystemDefined" cols="6">
                <h3>Operations system model</h3>
                <br />
                <exo-input-ref-notparametric
                  :root="_self"
                  name="antennaDefinition"
                  type="antennaDefinition"
                  componentRoute="/numericalantennadefinition"
                />
                <exo-input-ref-notparametric
                  :root="_self"
                  name="payload"
                  type="payload"
                  componentRoute="/analyticalConstellationPayload"
                />
                <exo-form-alert type="warning" v-if="inputs.payload != null">
                  The payload's field of view will not be used for
                  <b>Simulation missions </b>.
                </exo-form-alert>
                <div v-if="inputs.antennaDefinition != null">
                  <exo-input-ref-multiple
                    :root="_self"
                    name="groundStations"
                    :isParametric="false"
                    type="groundStation"
                    componentRoute="/analyticalconstellationgroundstation"
                  />
                </div>
                <div v-if="inputs.payload != null">
                  <exo-input-ref-multiple
                    :root="_self"
                    name="earthMeshs"
                    :isParametric="false"
                    type="earthMesh"
                    componentRoute="/analyticalconstellationearthmesh"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-if="inputs.dragModelDefined || inputs.srpModelDefined">
          <v-col cols="12">
            <v-row>
              <v-col v-if="inputs.dragModelDefined" cols="6">
                <h3>Drag model</h3>
                <br />
                <exo-input-double :root="_self" name="dragCoefficient">
                  <exo-help-dialog title="Drag coefficient">
                    <tooltipDragCoefficient />
                  </exo-help-dialog>
                </exo-input-double>
                <exo-input-double :root="_self" name="liftRatio">
                  <exo-help-dialog title="Lift ratio">
                    <tooltipLiftRatio />
                  </exo-help-dialog>
                </exo-input-double>
              </v-col>
              <v-col v-if="inputs.srpModelDefined" cols="6">
                <h3>Sun radiation pressure model</h3>
                <br />
                <exo-input-double :root="_self" name="absorptionCoefficient">
                  <exo-help-dialog title="Absorption coefficient">
                    <tooltipAbsorptionCoefficient />
                  </exo-help-dialog>
                </exo-input-double>
                <exo-input-double :root="_self" name="reflectionCoefficient">
                  <exo-help-dialog title="Specular reflection coefficient">
                    <tooltipReflectionCoefficient />
                  </exo-help-dialog>
                </exo-input-double>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-if="inputs.attitudeModelDefined" :root="_self">
          <v-col cols="12">
            <v-row>
              <v-col cols="6">
                <h3>Attitude model</h3>
                <br />
                <exo-input-double
                  v-if="inputs.attitudeModelDefined"
                  :root="_self"
                  name="maxAngularVelocity"
                />
                <v-row>
                  <v-col cols="8">
                    <exo-input-double
                      v-if="inputs.attitudeModelDefined"
                      :root="_self"
                      name="maxAngularAcceleration"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-switch
                      v-model="enableMaxAngularAccelerationFinder"
                      label="finder"
                    />
                  </v-col>
                </v-row>
                <exo-input-ref-multiple
                  :root="_self"
                  name="reactionWheels"
                  :isParametric="false"
                  type="reactionWheelDefinition"
                  componentRoute="/reactionwheeldefinition"
                >
                  <exo-help-dialog title="Reaction wheel">
                    <tooltipReactionWheel />
                  </exo-help-dialog>
                </exo-input-ref-multiple>
              </v-col>
              <v-col cols="6">
                <v-card
                  class="pa-2 mb-2 elevation-4"
                  v-if="enableMaxAngularAccelerationFinder"
                >
                  <h3 class="blue-grey--text m-0 p-0">
                    Maximum angular acceleration finder
                  </h3>
                  <br />
                  <v-row>
                    <v-col cols="4">
                      <v-text-field
                        v-model="j1"
                        label="J1 (kg.m²)"
                        type="number"
                      />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="j2"
                        label="J2 (kg.m²)"
                        type="number"
                      />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="j3"
                        label="J3 (kg.m²)"
                        type="number"
                      />
                    </v-col>
                  </v-row>
                  <v-text-field
                    v-model="torque"
                    label="Omnidirectional actuator torque (N.m)"
                    type="number"
                  />
                  <v-text-field
                    v-model="maxAngularAccelerationResult"
                    disabled
                    outlined
                    label="Maximum angular acceleration (deg/s²)"
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import dataTools from '../../tools/data.js'
import tooltipDutyCycleAnalytical from '../../components/Tooltips/tooltipDutyCycleAnalytical'
import tooltipOnBoardAveragePower from '../../components/Tooltips/Simulations/tooltipOnBoardAveragePower'
import tooltipDutyCycleNumerical from '../../components/Tooltips/Simulations/tooltipDutyCycleNumerical'
import tooltipAdditionalSystem from '../../components/Tooltips/Simulations/tooltipAdditionalSystem'
import tooltipSpacecraftShape from '../../components/Tooltips/Simulations/tooltipSpacecraftShape'
import tooltipPlatformPower from '../../components/Tooltips/Simulations/tooltipPlatformPower'
import tooltipDragCoefficient from '../../components/Tooltips/Simulations/tooltipDragCoefficient'
import tooltipLiftRatio from '../../components/Tooltips/Simulations/tooltipLiftRatio'
import tooltipAbsorptionCoefficient from '../../components/Tooltips/Simulations/tooltipAbsorptionCoefficient'
import tooltipReflectionCoefficient from '../../components/Tooltips/Simulations/tooltipReflectionCoefficient'
import tooltipReactionWheel from '../../components/Tooltips/Simulations/tooltipReactionWheel'
import tooltipThrustDuration from '../../components/Tooltips/Simulations/tooltipThrustDuration'

export default {
  name: 'spacecraft',

  components: {
    tooltipDutyCycleAnalytical,
    tooltipOnBoardAveragePower,
    tooltipDutyCycleNumerical,
    tooltipAdditionalSystem,
    tooltipSpacecraftShape,
    tooltipPlatformPower,
    tooltipDragCoefficient,
    tooltipLiftRatio,
    tooltipAbsorptionCoefficient,
    tooltipReflectionCoefficient,
    tooltipReactionWheel,
    tooltipThrustDuration
  },

  mixins: [formMixin, numericalMixin],

  mounted() {
    this.refreshReferences()
  },

  computed: {
    maxAngularAccelerationResult() {
      const j1 = parseFloat(this.j1)
      const j2 = parseFloat(this.j2)
      const j3 = parseFloat(this.j3)
      const jmax = Math.max(j1, j2, j3)
      const torque = parseFloat(this.torque)
      if (torque === 0) {
        return 0
      }
      if (jmax > 0) {
        return ((torque / jmax) * 180) / Math.PI
      } else {
        return Infinity
      }
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'platformMass',
          text: 'Platform mass' + this.getDefaultUnitLabel('platformMass'),
          sortable: true
        },
        {
          value: 'onBoardAveragePower',
          text:
            'Orbital average power for propulsion' +
            this.getDefaultUnitLabel('onBoardAveragePower'),
          sortable: true
        },
        {
          value: 'dutyCycle',
          text: 'Duty cycle' + this.getDefaultUnitLabel('dutyCycle'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      thrustersNotParametrics: [],
      batteryDefinitionsNotParametrics: [],
      reactionWheelDefinitionsNotParametrics: [],
      antennaDefinitionsNotParametrics: [],
      payloadsNotParametrics: [],
      groundStationsNotParametrics: [],
      earthMeshsNotParametrics: [],
      spacecraftGeometrysNotParametrics: [],

      // For maximum angular acceleration finder
      j1: 0,
      j2: 0,
      j3: 0,
      torque: 0,
      enableMaxAngularAccelerationFinder: false,

      modalRef: { content: [] },
      showModal: false,

      inputs: {
        platformMass: this.getDefaultValue('platformMass'),
        onBoardAveragePower: this.getDefaultValue('onBoardAveragePower'),
        platformPower: this.getDefaultValue('platformPower'),
        dutyCycle: this.getDefaultValue('dutyCycle'),
        thrustDuration: this.getDefaultValue('thrustDuration'),
        powerSystemDefined: this.getDefaultValue('powerSystemDefined'),
        operationSystemDefined: this.getDefaultValue('operationSystemDefined'),
        dragModelDefined: this.getDefaultValue('dragModelDefined'),
        srpModelDefined: this.getDefaultValue('srpModelDefined'),
        attitudeModelDefined: this.getDefaultValue('attitudeModelDefined'),
        thruster: this.getDefaultValue('thruster'),
        batteryDefinition: this.getDefaultValue('batteryDefinition'),
        reactionWheels: [],
        antennaDefinition: this.getDefaultValue('antennaDefinition'),
        payload: this.getDefaultValue('payload'),
        groundStations: [],
        earthMeshs: [],
        spacecraftGeometry: this.getDefaultValue('spacecraftGeometry'),
        dragCoefficient: this.getDefaultValue('dragCoefficient'),
        liftRatio: this.getDefaultValue('liftRatio'),
        absorptionCoefficient: this.getDefaultValue('absorptionCoefficient'),
        reflectionCoefficient: this.getDefaultValue('reflectionCoefficient'),
        maxAngularVelocity: this.getDefaultValue('maxAngularVelocity'),
        maxAngularAcceleration: this.getDefaultValue('maxAngularAcceleration'),
        scope: this.getDefaultValue('scope')
      }
    }
  },

  methods: {
    getRequiredPropertiesList(instance) {
      if (this.getCurrentFormDescription().required == null) {
        return []
      }
      let requiredList = this.getCurrentFormDescription().required.slice()
      if (this.inputs.powerSystemDefined) {
        requiredList.push('platformPower')
        requiredList.push('batteryDefinition')
        requiredList.push('spacecraftGeometry')
      }
      if (this.inputs.dragModelDefined) {
        requiredList.push('dragCoefficient')
        requiredList.push('liftRatio')
        requiredList.push('spacecraftGeometry')
      }
      if (this.inputs.srpModelDefined) {
        requiredList.push('absorptionCoefficient')
        requiredList.push('reflectionCoefficient')
        requiredList.push('spacecraftGeometry')
      }
      if (this.inputs.attitudeModelDefined) {
        requiredList.push('maxAngularVelocity')
        requiredList.push('maxAngularAcceleration')
      }
      if (this.inputs.antennaDefinition) {
        requiredList.push('groundStations')
      }
      if (this.inputs.payload) {
        requiredList.push('earthMeshs')
      }
      return requiredList
    },

    refreshReferences() {
      this.refreshThrusters()
      this.refreshBatteryDefinitions()
      this.refreshReactionWheels()
      this.refreshAntennaDefinitions()
      this.refreshpayloads()
      this.refreshSpacecraftGeometrys()
      this.refreshGroundStations()
      this.refreshEarthMeshs()
    },

    refreshThrusters() {
      let self = this
      this.$http
        .get(this.getAPI('propulsionsystemdefinitions'))
        .then((response) => {
          self.thrustersNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of thrusters. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshAntennaDefinitions() {
      let self = this
      this.$http
        .get(this.getAPI('antennadefinitions'))
        .then((response) => {
          self.antennaDefinitionsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of antennas. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshGroundStations() {
      // TODO MFI automatic method for all of these getters
      let self = this
      this.$http
        .get(this.getAPI(`groundstationdefinitions`))
        .then((response) => {
          self.groundStationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of groundStations. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshEarthMeshs() {
      let self = this
      this.$http
        .get(this.getAPI(`earthmeshdefinitions`))
        .then((response) => {
          self.earthMeshsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of earth meshes. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshBatteryDefinitions() {
      let self = this
      this.$http
        .get(this.getAPI('batterydefinitions'))
        .then((response) => {
          self.batteryDefinitionsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of batteries. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshReactionWheels() {
      let self = this
      this.$http
        .get(this.getAPI('reactionwheeldefinitions'))
        .then((response) => {
          self.reactionWheelDefinitionsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of reaction wheel. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshpayloads() {
      // TODO MFI automatic method for all of these getters
      let self = this
      this.$http
        .get(this.getAPI(`payloaddefinitions`))
        .then((response) => {
          self.payloadsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of payloads. ',
            e,
            'notification',
            'error'
          )
        })
    },
    refreshSpacecraftGeometrys() {
      let self = this
      this.$http
        .get(this.getAPI('spacecraftgeometrydefinitions'))
        .then((response) => {
          self.spacecraftGeometrysNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of spacecraft geometries. ',
            e,
            'notification',
            'error'
          )
        })
    },

    getMaxThrustDuration() {
      if (this.inputs.thruster) {
        const maxThrustDuration = this.inputs.thruster.maximumThrustDuration
        if (maxThrustDuration !== '') {
          return maxThrustDuration / 60
        }
      }
      return 20
    },

    getTotalMass() {
      let totalMass = this.inputs.platformMass
      if (this.inputs.thruster) {
        totalMass = totalMass + this.inputs.thruster.totalMass
      }
      return totalMass
    },

    getMaxDeltaV() {
      if (this.inputs.thruster) {
        const maxThrustDuration = this.inputs.thrustDuration
        const thrusterConsumption =
          this.inputs.thruster.thrust / (this.inputs.thruster.isp * 9.80665)
        const consumedMass = maxThrustDuration * thrusterConsumption * 60
        const maxDeltaV =
          this.inputs.thruster.isp *
          9.80665 *
          Math.log(this.getTotalMass() / (this.getTotalMass() - consumedMass))
        this.inputs.onBoardAveragePower = this.inputs.thruster.power * 0.9
        if (isNaN(maxDeltaV)) {
          return Infinity
        }
        return dataTools.displayNdigits(maxDeltaV, 3)
      }
      return 0
    },

    getMaxDutyCycle() {
      if (this.inputs.thruster) {
        const maxDutyCycle = dataTools.computeMaxDutyCycle(
          this.inputs.onBoardAveragePower,
          this.inputs.thruster.power,
          this.inputs.thruster.standbyPower
        )
        if (maxDutyCycle !== '') {
          return maxDutyCycle
        }
      }
      return 100
    },

    getMaxUnalteredDutyCycle() {
      // threshold for the possibility of the warmup changing the value of the duty cycle
      if (this.inputs.thruster) {
        const maxUnalteredDutyCycle = dataTools.computeMaxDutyCycleWithWarmup(
          this.inputs.onBoardAveragePower,
          this.inputs.thruster.power,
          this.inputs.thruster.standbyPower,
          this.inputs.thruster.warmUpPower,
          this.maxWarmupDutyCycle()
        )
        if (maxUnalteredDutyCycle !== '') {
          return maxUnalteredDutyCycle
        }
      }
      return 100
    },

    maxWarmupDutyCycle() {
      // maximum possible value of warmup duty cycle, that considers a circular orbit with altitude of 0 and default maneuver strategy
      const earthMU = 3.986004415e14
      const earthRadius = 6378136.3
      const initialOrbitPeriod =
        2 * Math.PI * earthRadius * Math.sqrt(earthRadius / earthMU)
      const arcsPerCycle = 2
      return (
        (arcsPerCycle * this.inputs.thruster.warmUpDuration) /
        initialOrbitPeriod
      )
    },

    dutyCycleUpdate() {
      this.inputs.dutyCycle = this.getMaxDutyCycle()
    },

    getCurrentFormType() {
      return 'SpacecraftDefinition'
    },

    getRefList(param) {
      return []
    },

    isExoInputMultiple(item) {
      return (
        item === 'groundStations' ||
        item === 'earthMeshs' ||
        item === 'reactionWheels'
      )
    }
  }
}
</script>
